import { useCallback, useMemo, useState } from 'react'

import type { AppearanceStyle } from '@tribeplatform/design-system/types'
import { SearchInput } from '@tribeplatform/react-ui-kit/Input'
import { useThemeContext } from '@tribeplatform/react-ui-kit/ThemeProvider'

import { useI18n } from '../i18n/index.js'

import { IconPickerCategory } from './components/IconPickerCategory.js'
import { PickerIcon } from './types.js'
import {
  addFrequentlyUsedIcon,
  getFrequentlyUsedIcons,
} from './utils/frequently-used.js'
import { getIcons } from './utils/icon-picker.js'

const frequentlyUsedIconNames = getFrequentlyUsedIcons() ?? []

interface IconPickerProps {
  iconStyle?: AppearanceStyle['icons']
  onIconChange: (icon: PickerIcon) => void
}

export const IconPicker = ({ iconStyle, onIconChange }: IconPickerProps) => {
  const { $t } = useI18n()
  const { style } = useThemeContext()

  const [query, setQuery] = useState('')

  const allIcons = useMemo(
    () => getIcons(iconStyle ?? style.icons),
    [style.icons, iconStyle],
  )

  const frequentlyUsedIcons = useMemo(
    () => allIcons.filter(icon => frequentlyUsedIconNames.includes(icon.name)),
    [allIcons],
  )
  const filteredIcons = allIcons.filter(icon => icon.name.includes(query)) ?? []

  const handleIconChange = useCallback(
    (icon: PickerIcon) => {
      onIconChange(icon)
      addFrequentlyUsedIcon(icon)
    },
    [onIconChange],
  )

  const icons = useMemo(
    () =>
      Object.fromEntries([
        [
          $t({
            defaultMessage: 'Frequently used',
            id: 'IconPicker.FrequentlyUsed',
          }),
          frequentlyUsedIcons,
        ],
        [
          $t({
            defaultMessage: 'Icons',
            id: 'IconPicker.Icons',
          }),
          allIcons,
        ],
      ]),
    [$t, allIcons, frequentlyUsedIcons],
  )

  return (
    <div className="w-full flex flex-col bg-surface text-content">
      <div className="py-2 px-3">
        <SearchInput
          onChange={e => setQuery(e.target.value)}
          value={query}
          placeholder={$t({
            defaultMessage: 'Search...',
            id: 'Generics.SearchDotDotDot',
          })}
          size="compact"
        />
      </div>
      <div className="flex-1 overflow-y-scroll">
        {!query ? (
          <IconPickerCategory icons={icons} onIconChange={handleIconChange} />
        ) : (
          <IconPickerCategory
            icons={Object.fromEntries([
              [
                $t({
                  defaultMessage: 'Search results',
                  id: 'IconPicker.SearchResults',
                }),
                filteredIcons,
              ],
            ])}
            onIconChange={handleIconChange}
          />
        )}
      </div>
    </div>
  )
}
